import { ReactElement } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
	blockTable,
	category,
	chartBar,
	cloud,
	cog,
	commentAuthorAvatar,
	currencyDollar,
	globe,
	help,
	home,
	institution,
	key,
	pages,
	people,
	receipt,
	shield,
	update,
} from '@wordpress/icons';
import { useTranslate } from 'i18n-calypso';

import feature from 'lib/utils/feature';
import useHostPartnerQuery from 'data/signup/use-host-partner-query';
import useWPCloudPartnershipStatus from 'lib/use-wpcloud-partnership-status';
import useHasSinglePartnership from 'lib/use-has-single-partnership';

export interface ShellNavItem {
	key: string;
	label: string;
	/** Leading icon, drawn in the item's text colour. */
	icon?: ReactElement;
	/** Internal route. */
	to?: string;
	/** External URL. */
	href?: string;
	/** Link target for `href` items. Defaults to a new tab. */
	target?: '_blank' | '_self';
	/** Only highlight on an exact route match (react-router `end`). Needed when `to` is a prefix of sibling routes. */
	end?: boolean;
	disabled?: boolean;
	/** Nested items, revealed while `to` (or a child) is the current route. */
	children?: ShellNavItem[];
}

export interface ShellNav {
	/** Accessible name for the `<nav>`. */
	label: string;
	items: ShellNavItem[];
	/** Secondary links rendered at the bottom of the nav. */
	footer: ShellNavItem[];
	/** External resources: in the header on wide viewports, in the drawer on narrow ones. */
	resources: ShellNavItem[];
}

// The same glyphs the legacy sidebars used, keyed by item.
const ICONS: Record< string, ReactElement > = {
	overview: blockTable,
	insights: chartBar,
	assistant: commentAuthorAvatar,
	inventory: globe,
	anomalies: shield,
	'api-access': cloud,
	'api-keys': key,
	billing: currencyDollar,
	'billing-summary': update,
	'billing-invoices': receipt,
	'billing-payment-methods': institution,
	'billing-pricing': currencyDollar,
	team: people,
	support: help,
	partnership: cog,
	partnerships: category,
	'field-guide': pages,
};

/**
 * Route-aware navigation model for the redesigned shell.
 *
 * One place for the visibility / disabled rules that the legacy sidebars each carry separately
 * (`sections/wpcloud/sidebar.tsx`, `sections/wpcloud/billing/sidebar.tsx`, `sections/jetpack/sidebar.tsx`,
 * `sections/overview/sidebar.tsx`). Those become dead code once the `wpcloud-redesign` flag is permanent.
 */
export default function useShellNav(): ShellNav {
	const translate = useTranslate();
	const { pathname } = useLocation();
	const [ searchParams ] = useSearchParams();
	const { data: partner, isSuccess } = useHostPartnerQuery();
	const status = useWPCloudPartnershipStatus();
	const hasSinglePartnership = useHasSinglePartnership();

	const isAutomattician = partner?.is_automattician ?? false;
	const approved = isAutomattician || status === 'approved';

	// External resources, available once the partnership is approved (as in the legacy sidebar).
	const resources: ShellNavItem[] = [
		...( feature( 'wpcloud-field-guide-section' ) && approved
			? [ { key: 'field-guide', label: String( translate( 'WP Cloud Documentation' ) ), icon: ICONS[ 'field-guide' ], href: 'https://wp.cloud/docs/' } ]
			: [] ),
	];

	// Users with several partnerships get a way back to the switcher; the header logo also links there.
	const footer: ShellNavItem[] = hasSinglePartnership
		? []
		: [ { key: 'partnerships', label: String( translate( 'All partnerships' ) ), icon: ICONS.partnerships, to: '/' } ];

	if ( pathname.startsWith( '/wpcloud' ) ) {
		const role = partner?.current_user_role ?? 'owner';
		// Automatticians get read-only access to all pages regardless of role/status.
		const canViewOwnerPages = isAutomattician || role === 'owner';
		const canViewManagerPages = isAutomattician || [ 'owner', 'manager' ].includes( role );
		const showAssistant = isAutomattician || (
			feature( 'wpcloud-assistant-section' ) && feature( 'wpcloud-agenttic-ui' )
		);

		// Preserve the selected client across navigation for Automatticians.
		const clientId = isAutomattician ? searchParams.get( 'atomic_client_id' ) : null;
		const withClient = ( path: string ) =>
			clientId ? `${ path }?atomic_client_id=${ encodeURIComponent( clientId ) }` : path;
		const item = ( key: string, label: string, path: string, extra: Partial< ShellNavItem > = {} ): ShellNavItem =>
			( { key, label, icon: ICONS[ key ], to: withClient( path ), ...extra } );

		const items: ShellNavItem[] = [];

		if ( canViewOwnerPages ) {
			items.push( item( 'overview', String( translate( 'Account Overview' ) ), '/wpcloud', { end: true } ) );
		}
		items.push( item( 'insights', String( translate( 'Insights' ) ), '/wpcloud/site-metrics', {
			disabled: ! feature( 'wpcloud-site-metrics-section' ) || ! approved,
		} ) );
		if ( showAssistant ) {
			items.push( item( 'assistant', String( translate( 'AI Assistant' ) ), '/wpcloud/assistant' ) );
		}
		items.push( item( 'inventory', String( translate( 'Inventory' ) ), '/wpcloud/inventory', {
			disabled: ! feature( 'wpcloud-inventory-section' ) || ! approved,
		} ) );
		if ( canViewManagerPages && ( isAutomattician || feature( 'wpcloud-warden-section' ) ) && approved ) {
			items.push( item( 'anomalies', String( translate( 'Anomalies' ) ), '/wpcloud/warden' ) );
		}
		if ( canViewManagerPages && feature( 'wpcloud-request-api-access-section' ) ) {
			items.push( item( 'api-access', String( translate( 'Request API Access' ) ), '/wpcloud/request-api-access', { disabled: ! approved } ) );
		}
		if ( canViewOwnerPages && feature( 'wpcloud-api-keys-section' ) ) {
			items.push( item( 'api-keys', String( translate( 'API Keys' ) ), '/wpcloud/api-keys', { disabled: ! approved } ) );
		}
		if ( canViewOwnerPages ) {
			items.push( item( 'billing', String( translate( 'Billing' ) ), '/wpcloud/billing', {
				end: true,
				disabled: ! approved,
				children: [
					item( 'billing-summary', String( translate( 'Summary' ) ), '/wpcloud/billing', { end: true, disabled: ! approved } ),
					item( 'billing-invoices', String( translate( 'Invoices' ) ), '/wpcloud/billing/invoices', { disabled: ! approved } ),
					...( feature( 'wpcloud-payment-methods' )
						? [ item( 'billing-payment-methods', String( translate( 'Payment Methods' ) ), '/wpcloud/billing/payment-methods', { disabled: ! approved } ) ]
						: [] ),
					item( 'billing-pricing', String( translate( 'Pricing' ) ), '/wpcloud/billing/pricing', { disabled: ! approved } ),
				],
			} ) );
		}
		if ( canViewManagerPages ) {
			items.push( item( 'team', String( translate( 'Team' ) ), '/wpcloud/team', {
				disabled: ! feature( 'wpcloud-team-section' ) || ! approved,
			} ) );
			items.push( item( 'support', String( translate( 'Support Request' ) ), '/wpcloud/support-request', {
				disabled: ! feature( 'wpcloud-support-request-section' ) || ! approved,
			} ) );
		}
		if ( canViewOwnerPages ) {
			items.push( item( 'partnership', String( translate( 'Partnership Details' ) ), '/wpcloud/partnership-details', { disabled: ! approved } ) );
		}

		return { label: String( translate( 'WP Cloud' ) ), items, footer, resources };
	}

	if ( pathname.startsWith( '/jetpack' ) ) {
		return {
			label: String( translate( 'Jetpack' ) ),
			items: [
				{ key: 'account', label: String( translate( 'Account' ) ), icon: home, to: '/jetpack', end: true },
				{ key: 'licenses', label: String( translate( 'Licenses' ) ), icon: key, to: '/jetpack/licenses' },
			],
			footer,
			resources,
		};
	}

	// Overview ("/"), auth and error pages: the partnership switcher.
	const hasJetpack = !! partner?.host_partner?.partnerships?.jetpack;
	const hasA4A = !! partner?.host_partner?.partnerships?.a4a;

	return {
		label: String( translate( 'Partnerships' ) ),
		items: [
			{ key: 'wpcloud', label: String( translate( 'WP Cloud' ) ), icon: globe, to: '/wpcloud' },
			// Disabled until the partner loads, so they never point at the wrong destination.
			hasJetpack
				? { key: 'jetpack', label: String( translate( 'Jetpack' ) ), icon: globe, to: '/jetpack' }
				: { key: 'jetpack', label: String( translate( 'Jetpack' ) ), icon: globe, href: 'https://jetpack.com/jetpack-partners/', disabled: ! isSuccess },
			hasA4A
				? { key: 'agencies', label: String( translate( 'Agencies' ) ), icon: globe, href: 'https://agencies.automattic.com', target: '_self' }
				: { key: 'agencies', label: String( translate( 'Agencies' ) ), icon: globe, href: 'https://automattic.com/for-agencies/', disabled: ! isSuccess },
		],
		footer: [],
		resources,
	};
}
